import { t } from '@bia-auction/shared-i18n';
import { errorEnvelopeSchema } from '@bia-auction/shared-schema';
import { ApiError } from './api';
import { API_BASE } from './config';
import { ensureAccess } from './token-store';

export interface CardFields {
  holder: string;
  number: string;
  expiry: string;
  cvv: string;
}

/**
 * Initiate a 3DS payment (deposit or membership purchase). Both endpoints return the bank's
 * **self-submitting 3DS form as HTML**, not JSON — so this posts the (Bearer-authenticated)
 * request, then writes the returned HTML into the document so its auto-submit script navigates
 * the browser to the bank. After 3DS the bank calls the API callback, which redirects back to
 * `${PUBLIC_BIDDER_HOST}/payments/{success,fail,pending}` (ADR 0020). A non-2xx response is
 * surfaced as an {@link ApiError} (the page is left intact so the caller can show the message).
 */
export async function submit3ds(path: string, body: unknown): Promise<void> {
  const token = await ensureAccess();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 429) throw new ApiError(429, t('common.rateLimited'));
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const parsed = errorEnvelopeSchema.safeParse(await res.json());
      if (parsed.success) {
        // Turnstile failures carry a stable `error` label so they localize (the single-use token
        // often expires between challenge and submit); other errors keep the server message.
        message =
          parsed.data.error === 'Turnstile' ? t('common.turnstileFailed') : parsed.data.message;
      }
    } catch {
      // non-JSON body — keep the status text
    }
    throw new ApiError(res.status, message);
  }
  const html = await res.text();
  document.open();
  document.write(html);
  document.close();
}
